import React, { useMemo, useState } from "react";
import { COLORS, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "../theme.js";
import { Section, Seal, StatCard, EmptyState, Chip } from "../components/ui.jsx";
import { inputStyle, primaryBtnStyle, secondaryBtnStyle } from "../components/styles.js";
import { conditionColor } from "../domain/format.js";
import { maintenanceStatus } from "../domain/preservation.js";
import { STATUS_OPTIONS } from "../domain/status.js";
import { CATEGORIES, CATEGORY_KEYS, categoryMeta } from "../domain/categories.js";
import { SORT_OPTIONS, DEFAULT_SORT, sortItems } from "../domain/sorting.js";
import { readPref, writePref } from "../lib/prefs.js";

export default function PassportList({ items, onOpenItem, onEditItem, onAdd }) {
  const [q, setQ] = useState("");
  /* Edit mode retargets the whole list: tapping a row goes straight to its edit
     form instead of its detail screen. One visible control, rather than an edit
     affordance hidden on every row. */
  const [editMode, setEditMode] = useState(false);

  // Remembered per device, so the collection doesn't snap back to Newest on
  // every visit for someone who thinks of it alphabetically.
  const [sort, setSort] = useState(() => readPref("sort", DEFAULT_SORT));
  function chooseSort(key) {
    setSort(key);
    writePref("sort", key);
  }

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const matches = needle
      ? items.filter((i) => `${i.brand} ${i.model} ${i.colorway || ""}`.toLowerCase().includes(needle))
      : items;
    return sortItems(matches, sort);
  }, [items, q, sort]);

  const sortNote = SORT_OPTIONS.find((s) => s.key === sort)?.note;

  const avgCondition = items.length ? Math.round(items.reduce((s, i) => s + i.condition, 0) / items.length) : 0;
  const needCleaning = items.filter((i) => maintenanceStatus(i).cleanDue).length;
  const needRotation = items.filter((i) => maintenanceStatus(i).rotateDue).length;

  const statusCounts = STATUS_OPTIONS.map((s) => ({ ...s, count: items.filter((i) => i.status === s.key).length })).filter((s) => s.count > 0);
  const categoryCounts = CATEGORY_KEYS.map((k) => ({ meta: CATEGORIES[k], count: items.filter((i) => i.category === k).length })).filter((c) => c.count > 0);

  const pillStyle = { fontFamily: FONT_MONO, fontSize: 11, color: COLORS.creamDim, border: `1px solid ${COLORS.lineFaint}`, borderRadius: 20, padding: "4px 10px" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontFamily: FONT_DISPLAY, fontSize: 20, color: COLORS.cream }}>My Passport</span>
      </div>

      {items.length > 0 && (
        <Section title="Collection Status">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
            <StatCard label="Avg. condition" value={avgCondition} accent={conditionColor(avgCondition)} />
            <StatCard label="Need cleaning" value={needCleaning} accent={needCleaning ? COLORS.red : COLORS.cream} />
            <StatCard label="Need rotation" value={needRotation} accent={needRotation ? COLORS.red : COLORS.cream} />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: categoryCounts.length > 1 ? 10 : 0 }}>
            {statusCounts.map((s) => (
              <span key={s.key} style={pillStyle}>
                {s.label} · {s.count}
              </span>
            ))}
          </div>
          {categoryCounts.length > 1 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {categoryCounts.map((c) => (
                <span key={c.meta.key} style={pillStyle}>
                  {c.meta.icon} {c.meta.label} · {c.count}
                </span>
              ))}
            </div>
          )}
        </Section>
      )}

      {items.length > 0 && (
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your collection" style={inputStyle} />
      )}

      {/* Sorting one item is a control that does nothing, so it appears only
          once there is an order to change. */}
      {items.length > 1 && (
        <div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {SORT_OPTIONS.map((s) => (
              <Chip key={s.key} label={s.label} active={sort === s.key} onClick={() => chooseSort(s.key)} />
            ))}
          </div>
          <div style={{ fontSize: 11, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 8 }}>{sortNote}</div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map((item) => {
          const ms = maintenanceStatus(item);
          return (
            <div
              key={item.id}
              onClick={() => (editMode ? onEditItem(item.id) : onOpenItem(item.id))}
              style={{
                cursor: "pointer",
                background: COLORS.blackCard,
                border: `1px solid ${editMode ? COLORS.gold : COLORS.lineFaint}`,
                borderRadius: 14, padding: 14, display: "flex", alignItems: "center", gap: 14,
              }}
            >
              <Seal value={item.condition} size={50} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 15.5, color: COLORS.cream }}>
                  {item.brand} {item.model}
                </div>
                <div style={{ fontSize: 12, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 2 }}>
                  {categoryMeta(item.category).icon} {item.colorway ? `${item.colorway} · ` : ""}
                  {item.size ? `Size ${item.size}` : categoryMeta(item.category).label}
                </div>
              </div>
              {editMode ? (
                <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: COLORS.goldBright, border: `1px solid ${COLORS.gold}`, borderRadius: 20, padding: "4px 10px", flex: "none" }}>
                  Edit ›
                </span>
              ) : (
                ms.cleanDue && (
                  <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: COLORS.red, border: `1px solid ${COLORS.red}`, borderRadius: 20, padding: "3px 8px", flex: "none" }}>
                    Clean due
                  </span>
                )
              )}
            </div>
          );
        })}

        {items.length === 0 && (
          <EmptyState title="Nothing catalogued yet" body="Every item you add gets a condition seal, a wear log and a preservation profile built from its materials." />
        )}
        {items.length > 0 && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "30px 0", color: COLORS.creamFaint, fontFamily: FONT_BODY, fontSize: 13 }}>No items match your search.</div>
        )}
      </div>

      {items.length > 0 && (
        <button
          onClick={() => setEditMode((v) => !v)}
          style={{
            ...secondaryBtnStyle,
            marginTop: 4,
            borderColor: editMode ? COLORS.gold : COLORS.line,
            color: editMode ? COLORS.goldBright : COLORS.cream,
            background: editMode ? "rgba(201,162,39,0.12)" : "transparent",
          }}
        >
          {editMode ? "Done editing" : "✎ Edit Items"}
        </button>
      )}

      {editMode && (
        <div style={{ fontSize: 11.5, color: COLORS.creamFaint, fontFamily: FONT_BODY, textAlign: "center", marginTop: -6 }}>
          Tap any item to edit or delete it.
        </div>
      )}

      <button onClick={onAdd} style={{ ...primaryBtnStyle, marginTop: 4 }}>
        + Add New Item
      </button>
    </div>
  );
}
